import { createContext, useContext, useEffect, useState, type ReactNode } from "react"

// -----------------------------------------------------------------------------
// Language
// -----------------------------------------------------------------------------
export type Lang = "es" | "en"

const messages = {
  es: {
    meta: {
      title: "Blumen Solutions — Hecho en México, pensado para el mundo.",
      description:
        "Blumen Solutions crea software de negocio enfocado para organizaciones de cualquier tamaño, industria y país. Una familia de productos — Rewards, Events y más.",
    },
    nav: {
      work: "Trabajo",
      products: "Productos",
      studio: "Estudio",
      principles: "Principios",
      contact: "Contacto",
      language: "EN",
      cta: "Iniciar proyecto",
    },
    hero: {
      eyebrow: "// hola, somos blumen",
      tagline: "Software a la medida,",
      taglineEm: "producto propio.",
      lead:
        "Un estudio de software en México que diseña y construye productos enfocados — los nuestros y los de quienes elegimos como aliados.",
      ctaPrimary: "Conoce nuestro trabajo",
      ctaSecondary: "Iniciar proyecto",
      metaLabel: "Estado",
      metaValue: "Aceptando 2 proyectos para Q3",
      signalsLabel: "Señales del estudio",
      signals: [
        { icon: "map-pin", k: "Base", v: "Monterrey, MX" },
        { icon: "users", k: "Equipo", v: "De 4 a 8 personas" },
        { icon: "layers", k: "Entregados", v: "20+ proyectos" },
        { icon: "calendar", k: "Desde", v: "2022" },
      ] as const,
      availabilityLabel: "Disponibilidad · Q3",
      availabilitySlots: [
        { taken: true, label: "Reservado" },
        { taken: false, label: "Abierto" },
        { taken: false, label: "Abierto" },
      ] as const,
      services: [
        { label: "Producto & UX", icon: "compass" },
        { label: "Apps web", icon: "monitor" },
        { label: "iOS / Android", icon: "smartphone" },
        { label: "Integraciones", icon: "plug" },
        { label: "APIs & datos", icon: "database" },
        { label: "IA aplicada", icon: "sparkles" },
      ] as const,
    },
    work: {
      eyebrow: "Trabajo para clientes",
      title: "Proyectos que hemos entregado.",
      lead:
        "Una muestra del trabajo que hemos hecho para clientes en Latinoamérica — desde marcas D2C hasta equipos internos en empresas grandes. Dos plazas abiertas por trimestre.",
      cases: [
        {
          client: "Terralú",
          year: "2023",
          title: "Ecommerce D2C para una marca de cosmética natural.",
          tags: ["Shopify", "React", "Stripe"],
          href: "#",
          tone: "brand" as const,
        },
        {
          client: "Fundación Monterrey",
          year: "2024",
          title: "Portal de donaciones y gestión de voluntariado.",
          tags: ["Next.js", "Stripe", "Supabase"],
          href: "#",
          tone: "neutral" as const,
        },
        {
          client: "Cliente financiero · NDA",
          year: "2024",
          title: "Panel de conciliación y reporteo para un equipo de operaciones.",
          tags: ["React", "Postgres", "Auditoría"],
          href: null,
          tone: "dark" as const,
        },
        {
          client: "SaaS B2B · NDA",
          year: "2025",
          title: "Flujo de onboarding asistido por IA para equipos de ventas.",
          tags: ["Next.js", "OpenAI", "Postgres"],
          href: null,
          tone: "neutral" as const,
        },
      ],
    },
    products: {
      eyebrow: "Productos propios",
      title: "Una familia, un estándar.",
      lead:
        "Construimos software pensando a largo plazo. Cada producto Blumen reemplaza una hoja de cálculo, un chat de grupo o un parche que el equipo superó hace tiempo.",
      openLabel: "Ver detalles",
      closeLabel: "Cerrar",
      sections: {
        overview: "Resumen",
        features: "Capacidades",
        audience: "Para quién",
        stack: "Construido con",
        pricing: "Precios",
      },
      items: [
        {
          badge: "Disponible",
          label: "Rewards",
          title: "Lealtad digital para negocios reales.",
          copy:
            "Pases en Apple y Google Wallet, escaneo por QR y un panel que conecta cada sello con un cliente real.",
          meta: "Desde $299 MXN/mes",
          href: "https://blumenrewards.com",
          linkLabel: "Ir al sitio",
          overview:
            "Reemplaza la tarjeta de cartón con un pase nativo de Apple y Google Wallet. Cada sello se escanea por QR y se registra contra un cliente real — puedes ver cuántos regresan, quién está por redimir y cuándo conviene mandar un mensaje.",
          features: [
            "Pases nativos de Apple Wallet y Google Wallet",
            "Escaneo por QR desde iPad o teléfono del staff",
            "Mensajería push directa al pase del cliente",
            "Panel con métricas de retención y redenciones",
            "Múltiples sucursales y equipos por cuenta",
          ],
          audience:
            "Restaurantes, cafeterías, barberías, servicios y pequeñas cadenas que aún reparten tarjetas de cartón.",
          stack: ["Apple Wallet", "Google Wallet", "Stripe", "Next.js", "Supabase"],
          pricing:
            "Desde $299 MXN/mes · sin contrato anual · incluye onboarding asistido.",
        },
        {
          badge: "Disponible",
          label: "Events",
          title: "Gestión de eventos sin hojas de cálculo.",
          copy:
            "RSVP con marca propia, base de datos unificada de invitados y mensajería por WhatsApp y correo.",
          meta: "B2B · Multi-tenant",
          href: "#",
          linkLabel: "Agendar demo",
          overview:
            "Una plataforma multi-tenant para equipos que coordinan eventos con frecuencia. Construye páginas RSVP con tu marca, administra listas desde un solo lugar y manda actualizaciones por WhatsApp y correo sin salir del panel.",
          features: [
            "Páginas RSVP white-label con dominio propio",
            "Base de datos unificada de invitados entre eventos",
            "Mensajería por WhatsApp Business + correo transaccional",
            "Check-in por QR con staff roles y permisos",
            "Exportes y reportes para finanzas y operaciones",
          ],
          audience:
            "ONGs, agencias, equipos corporativos de marketing, venues y universidades que corren 5+ eventos al año.",
          stack: ["WhatsApp Business", "Postmark", "Next.js", "Supabase", "Vercel"],
          pricing:
            "B2B · multi-tenant · plan anual con sede corporativa + sub-cuentas.",
        },
        {
          badge: "Próximamente",
          label: "Más",
          title: "Y lo que siga.",
          copy:
            "La familia crece con cada flujo que los equipos modernos terminan superando.",
          meta: "En construcción",
          href: null,
          linkLabel: null,
          overview:
            "Analytics, facturación, inventario, punto de venta — siempre con la misma filosofía: enfoque, diseño cuidado y precio justo. Si tu equipo está peleando con una hoja de cálculo, escríbenos: probablemente ya está en el roadmap.",
          features: [
            "Analytics enfocado en PyMEs",
            "Facturación electrónica nativa México",
            "Inventario y punto de venta",
            "Módulos que se integran con Rewards y Events",
          ],
          audience:
            "Equipos que ya usan Rewards o Events y quieren cerrar el siguiente flujo.",
          stack: ["TBD"],
          pricing: "En construcción · aceptando early-access.",
        },
      ],
    },
    studio: {
      eyebrow: "El estudio",
      missionLabel: "Nuestra misión",
      missionBody:
        "Construir software enfocado y bien diseñado que reemplace las hojas de cálculo, las soluciones improvisadas y los sistemas fragmentados de los que dependen los equipos — desde México, para el mundo.",
      visionLabel: "Cómo trabajamos",
      visionBody:
        "Equipos pequeños. Ciclos de dos semanas. Diseño y código en la misma mesa. No vendemos horas, entregamos producto listo para operar — y nos quedamos para lo que sigue.",
      credit: "Foto del equipo · Blumen HQ",
    },
    principles: {
      eyebrow: "Principios",
      title: "Cómo construimos.",
      items: [
        {
          n: "01",
          icon: "target",
          t: "Enfoque antes que amplitud.",
          d: "Cada producto hace una cosa, bien. Preferimos terminar algo que deje a una PyME sin Excel antes que lanzar un módulo genérico.",
        },
        {
          n: "02",
          icon: "globe",
          t: "Localización de verdad.",
          d: "Inglés como primer idioma y soporte real de múltiples idiomas. Monedas, formatos de fecha y normas regionales tratados como ciudadanos de primera clase — no como adaptación.",
        },
        {
          n: "03",
          icon: "scale",
          t: "Precios sin tamaño de empresa.",
          d: "Una tienda de barrio y una cadena internacional merecen el mismo estándar de diseño. La diferencia está en lo que cobramos, no en lo que entregamos.",
        },
        {
          n: "04",
          icon: "feather",
          t: "Diseñado para no estorbar.",
          d: "La mejor herramienta es la que tu equipo olvida que está usando. Cada interfaz se audita por fricción antes que por funciones.",
        },
      ] as const,
    },
    clients: {
      title: "La caja de herramientas.",
      lead:
        "Nos apoyamos en un set opinionado de tecnologías y plataformas. Nada de tooling por moda — cada pieza está en producción en al menos dos proyectos vivos.",
      items: [
        "TypeScript",
        "React",
        "Next.js",
        "Tailwind CSS",
        "shadcn/ui",
        "Supabase",
        "PostgreSQL",
        "Vercel",
        "Stripe",
        "Apple Wallet",
        "Google Wallet",
        "WhatsApp Business",
        "Postmark",
        "OpenAI",
      ] as const,
    },
    ctaBand: {
      title: "¿Construyes algo que merece mejor software?",
      body:
        "Estamos en Monterrey, trabajando con equipos en toda Latinoamérica. Cuéntanos qué operación todavía vive en hojas de cálculo.",
      ctaPrimary: "Iniciar proyecto",
      ctaSecondary: "Escribirnos",
    },
    footer: {
      blurb:
        "Blumen Solutions es un estudio de software en México. Construimos productos propios y aceptamos un número selecto de proyectos cada trimestre.",
      products: "Productos",
      company: "Estudio",
      legal: "Legal",
      links: {
        rewards: "Blumen | Rewards",
        events: "Blumen | Events",
        roadmap: "Próximos productos",
        work: "Trabajo",
        mission: "Misión",
        principles: "Principios",
        contact: "Contacto",
        privacy: "Privacidad",
        terms: "Términos",
      },
      madeIn: "Hecho en México",
      rights: "Todos los derechos reservados.",
    },
  },

  en: {
    meta: {
      title: "Blumen Solutions — Made in Mexico, built for the world.",
      description:
        "Blumen Solutions builds focused business software for organizations of any size, industry, or country. A family of products — Rewards, Events, and more.",
    },
    nav: {
      work: "Work",
      products: "Products",
      studio: "Studio",
      principles: "Principles",
      contact: "Contact",
      language: "ES",
      cta: "Start a project",
    },
    hero: {
      eyebrow: "// hi, we're blumen",
      tagline: "Custom software,",
      taglineEm: "own products.",
      lead:
        "A software studio in Mexico that designs and ships focused products — ours, and those of a small set of clients we hand-pick each quarter.",
      ctaPrimary: "See our work",
      ctaSecondary: "Start a project",
      metaLabel: "Status",
      metaValue: "Accepting 2 projects for Q3",
      signalsLabel: "Studio signals",
      signals: [
        { icon: "map-pin", k: "Based in", v: "Monterrey, MX" },
        { icon: "users", k: "Team", v: "4 to 8 people" },
        { icon: "layers", k: "Shipped", v: "20+ projects" },
        { icon: "calendar", k: "Since", v: "2022" },
      ] as const,
      availabilityLabel: "Availability · Q3",
      availabilitySlots: [
        { taken: true, label: "Booked" },
        { taken: false, label: "Open" },
        { taken: false, label: "Open" },
      ] as const,
      services: [
        { label: "Product & UX", icon: "compass" },
        { label: "Web apps", icon: "monitor" },
        { label: "iOS / Android", icon: "smartphone" },
        { label: "Integrations", icon: "plug" },
        { label: "APIs & data", icon: "database" },
        { label: "Applied AI", icon: "sparkles" },
      ] as const,
    },
    work: {
      eyebrow: "Client work",
      title: "Projects we've delivered.",
      lead:
        "A sample of the work we've shipped for clients across Latin America — from D2C brands to internal tools at enterprise teams. Two slots open per quarter.",
      cases: [
        {
          client: "Terralú",
          year: "2023",
          title: "D2C ecommerce for a natural-cosmetics brand.",
          tags: ["Shopify", "React", "Stripe"],
          href: "#",
          tone: "brand" as const,
        },
        {
          client: "Fundación Monterrey",
          year: "2024",
          title: "Donation portal + volunteer management for an NGO.",
          tags: ["Next.js", "Stripe", "Supabase"],
          href: "#",
          tone: "neutral" as const,
        },
        {
          client: "Financial client · NDA",
          year: "2024",
          title: "Reconciliation + reporting dashboard for an ops team.",
          tags: ["React", "Postgres", "Audit"],
          href: null,
          tone: "dark" as const,
        },
        {
          client: "B2B SaaS · NDA",
          year: "2025",
          title: "AI-assisted onboarding flow for a mid-stage sales team.",
          tags: ["Next.js", "OpenAI", "Postgres"],
          href: null,
          tone: "neutral" as const,
        },
      ],
    },
    products: {
      eyebrow: "Own products",
      title: "One family, one standard.",
      lead:
        "We build software with the long view. Every Blumen product replaces a spreadsheet, a group chat, or a patchwork tool your team outgrew long ago.",
      openLabel: "View details",
      closeLabel: "Close",
      sections: {
        overview: "Overview",
        features: "Capabilities",
        audience: "Built for",
        stack: "Built with",
        pricing: "Pricing",
      },
      items: [
        {
          badge: "Available",
          label: "Rewards",
          title: "Digital loyalty for real businesses.",
          copy:
            "Apple and Google Wallet passes, QR check-in, and a dashboard that connects every stamp back to a real customer.",
          meta: "From $299 MXN/mo",
          href: "https://blumenrewards.com",
          linkLabel: "Visit site",
          overview:
            "Replaces cardboard stamp cards with a native Apple and Google Wallet pass. Every stamp is scanned by QR and tied back to a real customer — you see who's returning, who's close to redeeming, and when it's worth sending a message.",
          features: [
            "Native Apple Wallet and Google Wallet passes",
            "QR check-in from iPad or staff phone",
            "Push messaging delivered straight to the pass",
            "Retention and redemption analytics dashboard",
            "Multi-location and multi-staff support",
          ],
          audience:
            "Restaurants, cafes, barbershops, services, and small chains still handing out cardboard loyalty cards.",
          stack: ["Apple Wallet", "Google Wallet", "Stripe", "Next.js", "Supabase"],
          pricing:
            "From $299 MXN/mo · no annual contract · assisted onboarding included.",
        },
        {
          badge: "Available",
          label: "Events",
          title: "Event management without spreadsheets.",
          copy:
            "White-labeled RSVP pages, a unified guest database, and WhatsApp + email messaging built in.",
          meta: "B2B · Multi-tenant",
          href: "#",
          linkLabel: "Book a demo",
          overview:
            "A multi-tenant platform for teams that run events regularly. Build RSVP pages with your own brand, manage guest lists from one place, and send updates over WhatsApp and email without leaving the dashboard.",
          features: [
            "White-label RSVP pages on your own domain",
            "Unified guest database across every event",
            "WhatsApp Business + transactional email messaging",
            "QR check-in with staff roles and permissions",
            "Exports and reports for finance and operations",
          ],
          audience:
            "Non-profits, agencies, corporate marketing teams, venues, and universities running 5+ events a year.",
          stack: ["WhatsApp Business", "Postmark", "Next.js", "Supabase", "Vercel"],
          pricing:
            "B2B · multi-tenant · annual plans with a corporate HQ and sub-accounts.",
        },
        {
          badge: "Coming soon",
          label: "More",
          title: "And what comes next.",
          copy:
            "The family grows with every workflow modern teams outgrow.",
          meta: "In progress",
          href: null,
          linkLabel: null,
          overview:
            "Analytics, billing, inventory, POS — always with the same philosophy: focus, careful design, fair pricing. If your team is fighting a spreadsheet, write to us — it's probably already on the roadmap.",
          features: [
            "Focused analytics for SMBs",
            "Mexico-native electronic billing",
            "Inventory and point of sale",
            "Modules that plug into Rewards and Events",
          ],
          audience:
            "Teams already using Rewards or Events who want to close the next workflow.",
          stack: ["TBD"],
          pricing: "In progress · early-access openings.",
        },
      ],
    },
    studio: {
      eyebrow: "The studio",
      missionLabel: "Our mission",
      missionBody:
        "Build focused, well-designed software that replaces the spreadsheets, workarounds, and fragmented systems teams depend on — out of Mexico, for the world.",
      visionLabel: "How we work",
      visionBody:
        "Small teams. Two-week cycles. Design and code at the same table. We don't sell hours — we ship product ready to run, and we stay for what comes next.",
      credit: "Team photo · Blumen HQ",
    },
    principles: {
      eyebrow: "Principles",
      title: "How we build.",
      items: [
        {
          n: "01",
          icon: "target",
          t: "Focus over breadth.",
          d: "Each product does one thing, well. We'd rather finish something that retires a spreadsheet than ship a generic module.",
        },
        {
          n: "02",
          icon: "globe",
          t: "Localization for real.",
          d: "English as a first language with real multi-language support. Multiple currencies, date formats, and regional norms treated as first-class citizens — not as an afterthought.",
        },
        {
          n: "03",
          icon: "scale",
          t: "Pricing independent of scale.",
          d: "A neighborhood shop and an international chain deserve the same design standard. The difference shows in what we charge, not what we ship.",
        },
        {
          n: "04",
          icon: "feather",
          t: "Designed to get out of the way.",
          d: "The best tool is the one your team forgets they're using. Every interface is audited for friction before features.",
        },
      ] as const,
    },
    clients: {
      title: "The toolbox.",
      lead:
        "We lean on an opinionated set of technologies and platforms. No tooling-for-fashion — every piece is in production on at least two live projects.",
      items: [
        "TypeScript",
        "React",
        "Next.js",
        "Tailwind CSS",
        "shadcn/ui",
        "Supabase",
        "PostgreSQL",
        "Vercel",
        "Stripe",
        "Apple Wallet",
        "Google Wallet",
        "WhatsApp Business",
        "Postmark",
        "OpenAI",
      ] as const,
    },
    ctaBand: {
      title: "Building something that deserves better software?",
      body:
        "We're in Monterrey, working with teams across Latin America. Tell us what operation still lives in spreadsheets.",
      ctaPrimary: "Start a project",
      ctaSecondary: "Get in touch",
    },
    footer: {
      blurb:
        "Blumen Solutions is a software studio based in Mexico. We ship our own products and take on a small set of client projects every quarter.",
      products: "Products",
      company: "Studio",
      legal: "Legal",
      links: {
        rewards: "Blumen | Rewards",
        events: "Blumen | Events",
        roadmap: "Upcoming products",
        work: "Work",
        mission: "Mission",
        principles: "Principles",
        contact: "Contact",
        privacy: "Privacy",
        terms: "Terms",
      },
      madeIn: "Made in Mexico",
      rights: "All rights reserved.",
    },
  },
} as const

// -----------------------------------------------------------------------------
// Context
// -----------------------------------------------------------------------------
type Ctx = {
  lang: Lang
  setLang: (l: Lang) => void
  toggle: () => void
  t: (typeof messages)["es"]
}

const I18nContext = createContext<Ctx | null>(null)

const STORAGE_KEY = "blumen.lang"

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === "undefined") return "es"
    const saved = window.localStorage.getItem(STORAGE_KEY) as Lang | null
    return saved ?? "es"
  })

  useEffect(() => {
    const m = messages[lang]
    document.documentElement.lang = lang === "es" ? "es-MX" : "en-US"
    document.title = m.meta.title
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", m.meta.description)
    document
      .querySelector('meta[property="og:title"]')
      ?.setAttribute("content", m.meta.title)
    document
      .querySelector('meta[property="og:description"]')
      ?.setAttribute("content", m.meta.description)
    document
      .querySelector('meta[property="og:locale"]')
      ?.setAttribute("content", lang === "es" ? "es_MX" : "en_US")
    document
      .querySelector('meta[name="twitter:description"]')
      ?.setAttribute("content", m.meta.description)
    window.localStorage.setItem(STORAGE_KEY, lang)
  }, [lang])

  const setLang = (l: Lang) => setLangState(l)
  const toggle = () => setLangState((prev) => (prev === "es" ? "en" : "es"))

  return (
    <I18nContext.Provider value={{ lang, setLang, toggle, t: messages[lang] as Ctx["t"] }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error("useI18n must be used within <I18nProvider>")
  return ctx
}
